import { ApiError, type StaffMe } from '@central/api';
import { Shell, color, navItemStyle } from '@central/ui';
import { Alert, Badge, Button } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouteContext } from '@tanstack/react-router';
import { keys, meQuery, useApi } from '../api';
import { Brand } from './Brand';
import { navFor } from './nav';

export function AppLayout() {
  const ctx = useRouteContext({ from: '/app' });
  const api = useApi();
  // The route context holds the me loaded at navigation; a settings save
  // refetches it, so the email-off notice follows the saved transport.
  const { data: me = ctx.me } = useQuery(meQuery(api));
  const reseller = me.user.role === 'Reseller';
  // A reseller's badge counts its tier 1 threads; Rethink's leaves those out.
  const unread =
    useQuery({
      queryKey: keys.unreadCount,
      queryFn: () => (reseller ? api.partner.threads.unreadCount() : api.threads.unreadCount()),
      refetchInterval: 60_000,
    }).data?.threads ?? 0;
  // A reseller's footer shows its logo beside its name.
  const { data: profile } = useQuery({
    queryKey: keys.partnerProfile,
    queryFn: () => api.partner.profile(),
    enabled: reseller,
    staleTime: 5 * 60_000,
  });
  return (
    <Shell
      variant="console"
      brand={<Brand />}
      nav={navFor(me.user.role).map((item) => (
        <Link
          key={item.to}
          to={item.to}
          activeOptions={{ exact: item.to === '/' || item.to === '/partner' }}
          style={navItemStyle('console', false)}
          activeProps={{ style: navItemStyle('console', true), 'aria-current': 'page' }}
        >
          {item.label}
          {item.to === '/inbox' && unread > 0 && (
            <Badge size="sm" color="yellow" c="dark" ml="auto" aria-label={`${unread} unread`}>
              {unread}
            </Badge>
          )}
        </Link>
      ))}
      footer={<UserFooter me={me} logoUrl={profile?.logoUrl} />}
    >
      {!me.emailEnabled && (
        <Alert color="yellow">
          Email is off: invites, setup links and notifications are not emailed. Links are shown on
          screen.
        </Alert>
      )}
      <Outlet />
    </Shell>
  );
}

function UserFooter({ me, logoUrl }: { me: StaffMe; logoUrl?: string }) {
  const f = me.user.secondFactors;
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // A 401 means the session is already gone (the query client's 401 handler
  // clears the cache and goes to sign in). Any other failure leaves the
  // server session alive, so the user stays here and may retry: logout is
  // idempotent.
  const signOut = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      qc.clear();
      void navigate({ to: '/sign-in' });
    },
  });
  const failed =
    signOut.error !== null && !(signOut.error instanceof ApiError && signOut.error.status === 401);
  return (
    <>
      {me.user.name} · {me.user.role}
      {me.user.reseller && (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${me.user.reseller.name} logo`}
              width={20}
              height={20}
              style={{ objectFit: 'contain' }}
            />
          )}
          {me.user.reseller.name}
        </span>
      )}
      <br />
      <span style={{ color: color.consolePasskeyOk }}>
        {f.passkeys > 0 ? 'Passkey ✓' : f.totp ? 'Authenticator ✓' : ''}
      </span>
      <Button
        variant="subtle"
        color="gray"
        size="compact-sm"
        fullWidth
        justify="flex-start"
        mt={10}
        loading={signOut.isPending}
        onClick={() => signOut.mutate()}
      >
        Sign out
      </Button>
      {failed && (
        <div role="alert" style={{ marginTop: 8 }}>
          Couldn’t sign you out. You’re still signed in; try again.
        </div>
      )}
    </>
  );
}
